/**
 * Erfassungsregeln - Übersicht (read-only, Zugang über Strecken-Detail)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/AdminLayout';
import { Settings, Plus, RefreshCw, Search, ChevronRight, Info } from 'lucide-react';

interface LaundryClassEntity {
  id: string;
  code: string;
  name: string;
}

interface CaptureConfig {
  id: string;
  screen: 'FL' | 'WSA';
  lineId: string | null;
  lineName: string | null;
  requiresLaundryClass: boolean;
  allowedLaundryClasses: LaundryClassEntity[];
}

const SCREEN_LABELS: Record<'FL' | 'WSA', string> = {
  FL: 'Vorarbeiter (FL)',
  WSA: 'Waschstation (WSA)',
};

export default function CaptureConfigPage() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<CaptureConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/capture-configs', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConfigs(data.configs || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter + Sort
  const filtered = useMemo(() => {
    let result = configs;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(c =>
        SCREEN_LABELS[c.screen].toLowerCase().includes(s) ||
        c.lineName?.toLowerCase().includes(s)
      );
    }
    // Sortieren: FL vor WSA, dann Global vor Strecken-spezifisch
    return [...result].sort((a, b) => {
      if (a.screen !== b.screen) return a.screen === 'FL' ? -1 : 1;
      if (!a.lineId && b.lineId) return -1;
      if (a.lineId && !b.lineId) return 1;
      return (a.lineName || '').localeCompare(b.lineName || '', 'de');
    });
  }, [configs, search]);

  // Anzeige-Text für Zeile
  const getDisplayText = (config: CaptureConfig): string => {
    const screen = SCREEN_LABELS[config.screen];
    const line = config.lineName ? ` - ${config.lineName}` : ' - Global';
    return screen + line;
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Erfassungsregeln
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {configs.length} {configs.length === 1 ? 'Regel' : 'Regeln'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Link
              to="/admin/capture-config/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Regel
            </Link>
          </div>
        </div>

        {/* Hinweis */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Hinweis</p>
            <p className="mt-1">
              Erfassungsregeln werden auf der jeweiligen Strecken-Detailseite bearbeitet.
              Diese Übersicht zeigt alle konfigurierten Regeln.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Liste */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search ? 'Keine Treffer' : 'Noch keine Regeln definiert'}
              {!search && (
                <Link
                  to="/admin/capture-config/new"
                  className="block mx-auto mt-4 text-blue-600 hover:underline"
                >
                  Erste Regel anlegen
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map(config => (
                <div
                  key={config.id}
                  className="flex items-center px-4 py-3 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/admin/capture-config/${config.id}`)}
                >
                  <div className="flex-1">
                    <span className="font-medium">{getDisplayText(config)}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
